<template>
  <v-container>
    <v-container>
      <ForecastHeader :illustrative="illustrative" @open-methodology="drawer = true" />

      <ForecastMethodologyDrawer
        v-model="drawer"
        :card="metaCard"
        :equations="diagnosticsDoc ? diagnosticsDoc.equations || [] : []"
        :lineage="diagnosticsDoc ? diagnosticsDoc.lineage || [] : []"
        :limitations="diagnosticsDoc ? diagnosticsDoc.limitations || [] : []"
      />

      <!-- 1. controls -->
      <ForecastControls
        :countries="countries"
        :model-value="value"
        :endogenous="endogenous"
        :loading="isLoading"
        :meta="metaBadges"
        @update:model-value="onCountry"
        @update:endogenous="endogenous = $event"
        @reload="reload"
      />

      <!-- 2. editable consensus inputs -->
      <ForecastInputsTable
        v-if="rows.length"
        :quarters="quarters"
        :rows="rows"
        :originals="originals"
        :disabled-names="endogenous ? ['USA - S&P500'] : []"
        @update-cell="updateCell"
        @reset-row="resetRow"
        @reset-all="resetAll"
      />
      <div v-else class="lx-skel-grid" aria-busy="true">
        <div class="lx-skel lx-skel--row"></div>
        <div class="lx-skel lx-skel--row"></div>
        <div class="lx-skel lx-skel--row"></div>
      </div>

      <!-- 3. apply -->
      <v-row justify="end" class="mt-2">
        <v-col cols="12" class="d-flex justify-end align-center" style="gap: 12px">
          <span v-if="isLoading" class="lx-elapsed" aria-live="polite">Computing… <template v-if="elapsed >= 3">{{ elapsed }} s</template></span>
          <v-btn :disabled="isLoading || !rows.length" class="elevation-0 bg-primary" variant="elevated" @click="apply">Apply</v-btn>
        </v-col>
      </v-row>

      <hr class="lx-rule" />

      <!-- 4. results -->
      <ForecastStates v-if="status === 'loading'" state="loading" :elapsed-seconds="elapsed" />

      <ForecastStates v-else-if="status === 'error'" state="error" :message="errorMessage" :detail="forecastError && forecastError.detail" @retry="apply">
        <template #actions>
          <button type="button" class="lx-btn" @click="reload">Reload consensus</button>
        </template>
      </ForecastStates>

      <ForecastStates v-else-if="!hasResult" state="empty" />

      <template v-else>
        <ForecastResultsHeader
          :country-name="value && value.name ? value.name : ''"
          :variable="variable"
          :variables="result.variables"
          :observed-to="datePeriod[0]"
          :horizon="datePeriod[1]"
          :endogenous="appliedEndogenous"
          :scenario-label="scenarioLabel"
          :scenario-hash="hash"
          :model-version="modelVersion"
          :table="table"
          :intervals-available="intervalsAvailable"
          :show-bands="showBands"
          :level="level"
          @update:variable="variable = $event"
          @update:table="table = $event"
          @update:show-bands="showBands = $event"
          @update:level="level = $event"
        />

        <v-row>
          <v-col cols="12" v-if="table">
            <ForecastResultsTable
              :dates="result.dates"
              :rows="result.rows"
              :baseline-by-date="baselineMap"
              :show-delta="isAlternative"
              :intervals="tableIntervals"
              :show-intervals="showBands"
              :intervals-reason="intervalsReason"
            />
          </v-col>
          <v-col cols="12" v-show="!table">
            <div class="lx-two-col">
              <ForecastChart
                ref="chart"
                :label="variable"
                :observed="observedPairs"
                :baseline="baselinePairs"
                :alternative="alternativePairs"
                :consensus="consensusPairs"
                :bands="chartBands.baseline"
                :alt-bands="isAlternative ? chartBands.alternative : null"
                :show-bands="showBands"
                :selected-level="level"
                :band-method="intervalsMethodLabel"
                :origin="result.dates[0]"
              />
              <ForecastDrivers
                :block="driverBlock"
                :series="variable"
                :week="driverWeek"
                :weeks="driverWeeks"
                :scenario="isAlternative ? 'alternative' : 'baseline'"
                :equation="equationFor(variable)"
                :reason="driversReason"
                @update:week="driverWeek = $event"
              />
            </div>
          </v-col>
        </v-row>

        <ForecastStates
          v-if="v2Unavailable"
          state="partial"
          title="Forecast evidence not available"
          :message="'Prediction intervals and driver decomposition are not shown: ' + v2Unavailable"
          class="mt-3"
        />

        <div v-if="intervalWarning" class="lx-banner lx-banner--info mt-3" role="note">
          <div><span class="lx-banner__title">Interval check</span>{{ intervalWarning }}</div>
        </div>

        <div class="mt-4">
          <ModelPerformance :diagnostics="getForecastDiagnostics" :status="getDiagnosticsStatus" :reason="diagnosticsReason" :series="variable" />
        </div>

        <ForecastExportMenu class="mt-3" :chart-available="!table" :busy="exporting" :message="exportMessage" @action="onExport" />
      </template>
    </v-container>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import ForecastHeader from "../components/ForecastHeader.vue";
import ForecastMethodologyDrawer from "../components/ForecastMethodologyDrawer.vue";
import ForecastControls from "../components/ForecastControls.vue";
import ForecastInputsTable from "../components/ForecastInputsTable.vue";
import ForecastResultsHeader from "../components/ForecastResultsHeader.vue";
import ForecastResultsTable from "../components/ForecastResultsTable.vue";
import ForecastChart from "../components/ForecastChart.vue";
import ForecastStates from "../components/ForecastStates.vue";
import ForecastDrivers from "../components/ForecastDrivers.vue";
import ModelPerformance from "../components/ModelPerformance.vue";
import ForecastExportMenu from "../components/ForecastExportMenu.vue";
import { parseConsensusTable, parseForecastResult, parseSeriesMap, seriesToPairs, buildAppJson, slashDate } from "../lib/parse";
import { scenarioLabel, scenarioHash, changedInputCount } from "../lib/scenario";
import { alignBands, bandPairs, widthMonotonicity, methodLabel } from "../lib/intervals";
import { buildExportSheets } from "../lib/exportSheets";
import { downloadXlsx } from "../lib/xlsxWriter";

const clone = (x) => JSON.parse(JSON.stringify(x));

export default {
  name: "forecast-tool",
  components: {
    ForecastHeader,
    ForecastMethodologyDrawer,
    ForecastControls,
    ForecastInputsTable,
    ForecastResultsHeader,
    ForecastResultsTable,
    ForecastChart,
    ForecastStates,
    ForecastDrivers,
    ModelPerformance,
    ForecastExportMenu,
  },
  data() {
    return {
      value: {},
      endogenous: false,
      quarters: [],
      rows: [],
      originals: [],
      table: false,
      variable: "",
      showBands: true,
      level: 80,
      drawer: false,
      elapsed: 0,
      elapsedTimer: null,
      appliedRows: null, // the inputs the shown result was computed from
      appliedEndogenous: false,
      driverWeek: 52,
      exporting: false,
      exportMessage: "",
    };
  },
  computed: {
    ...mapGetters([
      "getcontriesConsensus",
      "getdataConsensus",
      "getForescartResult",
      "getUrlExport",
      "getLoadingApplyForecast",
      "getFeForecast",
      "getLastSemester",
      "getConsensusReportResult",
      "getConsensusReportResultEndogenize",
      "getForecastStatus",
      "getForecastError",
      "getForecastLoadStartedAt",
      "getForecastV2",
      "getForecastV2Status",
      "getForecastDiagnostics",
      "getDiagnosticsStatus",
    ]),
    countries() {
      return (this.getcontriesConsensus || []).filter((i) => i.forecastFilter === true);
    },
    status() {
      return this.getForecastStatus;
    },
    isLoading() {
      return this.status === "loading";
    },
    forecastError() {
      return this.getForecastError;
    },
    errorMessage() {
      const e = this.forecastError;
      return e ? `The server returned: ${e.message}. Your edited assumptions are kept.` : "";
    },
    result() {
      return parseForecastResult(this.getForescartResult);
    },
    hasResult() {
      return this.result.dates.length > 0;
    },
    datePeriod() {
      const d = this.result.dates;
      return d.length ? [slashDate(d[0]), slashDate(d[d.length - 1])] : ["", ""];
    },
    observedMap() {
      return parseSeriesMap(this.getLastSemester);
    },
    consensusMap() {
      return parseSeriesMap(this.getFeForecast);
    },
    legacyBaselineMap() {
      // Precomputed results file (the original page's baseline). Kept only as a last
      // resort: it is produced offline and can lag the deployed models.
      return this.appliedEndogenous ? parseSeriesMap(this.getConsensusReportResult) : parseSeriesMap(this.getConsensusReportResultEndogenize);
    },
    baselineSource() {
      if (this.v2 && Array.isArray(this.v2.series) && this.v2.series.length) return "model";
      if (!this.isAlternative && this.hasResult) return "run";
      return "legacy";
    },
    baselineMap() {
      // Janus baseline = the deployed model run on the unedited consensus table.
      // 1) the v2 document carries it explicitly; 2) with no edited input the run
      // itself is the baseline; 3) otherwise fall back to the precomputed file.
      if (this.baselineSource === "model") {
        const out = {};
        for (const s of this.v2.series) {
          const m = {};
          this.v2.dates.forEach((d, i) => {
            if (s.baseline[i] != null) m[d] = s.baseline[i];
          });
          out[s.label] = m;
        }
        return out;
      }
      if (this.baselineSource === "run") {
        const out = {};
        for (const r of this.result.rows) {
          const m = {};
          this.result.dates.forEach((d, i) => {
            if (r.data[i] != null) m[d] = r.data[i];
          });
          out[r.name] = m;
        }
        return out;
      }
      return this.legacyBaselineMap;
    },
    observedPairs() {
      return seriesToPairs(this.observedMap[this.variable]);
    },
    baselinePairs() {
      return seriesToPairs(this.baselineMap[this.variable]);
    },
    consensusPairs() {
      return seriesToPairs(this.consensusMap[this.variable]);
    },
    alternativePairs() {
      // The alternative line exists only for an edited scenario; otherwise the run is the baseline.
      if (!this.isAlternative) return [];
      const row = this.result.rows.find((r) => r.name === this.variable);
      return row ? this.result.dates.map((d, i) => [d, row.data[i]]).filter((p) => p[1] != null) : [];
    },
    isAlternative() {
      return this.appliedRows ? changedInputCount(this.appliedRows, this.originals) > 0 : false;
    },
    scenarioLabel() {
      return this.appliedRows ? scenarioLabel(this.appliedRows, this.originals) : "Scenario: Consensus";
    },
    v2() {
      const d = this.getForecastV2;
      return d && d.schema ? d : null;
    },
    v2Unavailable() {
      const d = this.getForecastV2;
      if (this.getForecastV2Status === "unavailable" && d && d.available === false) return d.reason;
      return "";
    },
    diagnosticsDoc() {
      const d = this.getForecastDiagnostics;
      return d && d.schema ? d : null;
    },
    diagnosticsReason() {
      const d = this.getForecastDiagnostics;
      return d && d.available === false ? d.reason : "";
    },
    modelVersion() {
      return (this.v2 && this.v2.meta.modelVersion) || (this.diagnosticsDoc && this.diagnosticsDoc.meta.modelVersion) || "";
    },
    illustrative() {
      return !!((this.v2 && this.v2.meta.illustrative) || (this.diagnosticsDoc && this.diagnosticsDoc.meta.illustrative));
    },
    metaCard() {
      const m = (this.diagnosticsDoc && this.diagnosticsDoc.meta) || (this.v2 && this.v2.meta) || {};
      return { modelVariant: m.modelVariant || (this.v2 && this.v2.meta.modelVariant), modelVersion: m.modelVersion, modelEstimatedAt: m.modelEstimatedAt, dataAsOf: m.dataAsOf };
    },
    metaBadges() {
      return { ...this.metaCard, horizonWeeks: 52, illustrative: this.illustrative };
    },
    hash() {
      if (!this.appliedRows || !this.value || !this.value.forecastTool) return "";
      return scenarioHash({ country: this.value.forecastTool, endogenous: this.appliedEndogenous, quarters: this.quarters, rows: this.appliedRows, modelVersion: this.modelVersion || null });
    },
    intervalsAvailable() {
      return !!(this.v2 && Array.isArray(this.v2.intervals) && this.v2.intervals.length);
    },
    alignedBands() {
      const dates = this.result.dates;
      return {
        baseline: alignBands(this.v2, this.variable, "baseline", dates),
        alternative: alignBands(this.v2, this.variable, "alternative", dates),
      };
    },
    chartBands() {
      const dates = this.result.dates;
      const toPairs = (bands) => {
        const out = {};
        for (const [level, b] of Object.entries(bands)) out[level] = bandPairs(dates, b);
        return Object.keys(out).length ? out : null;
      };
      return { baseline: toPairs(this.alignedBands.baseline), alternative: toPairs(this.alignedBands.alternative) };
    },
    tableIntervals() {
      if (!this.v2) return null;
      const scenario = this.isAlternative ? "alternative" : "baseline";
      const out = {};
      for (const r of this.result.rows) {
        const b = alignBands(this.v2, r.name, scenario, this.result.dates)[this.level];
        if (b) out[r.name] = { level: this.level, lower: b.lower, upper: b.upper };
      }
      return out;
    },
    intervalsReason() {
      if (this.v2Unavailable) return this.v2Unavailable;
      if (this.v2 && this.v2.unavailable) {
        const u = this.v2.unavailable.find((x) => x.block === `intervals.${this.variable}`);
        if (u) return u.reason;
      }
      return "";
    },
    intervalsMethodLabel() {
      if (!this.v2) return "";
      if (this.v2.intervalsMethodLabel) return this.v2.intervalsMethodLabel;
      const b = this.alignedBands.baseline[this.level];
      return b ? methodLabel(this.level, b) : "";
    },
    intervalWarning() {
      const b = this.alignedBands.baseline[this.level];
      const m = widthMonotonicity(b);
      if (!m || m.firstShrink < 0) return "";
      return `The ${this.level}% interval for ${this.variable} narrows at week ${m.firstShrink} (${Math.round(m.share * 100)}% of steps widen). This is reported as delivered by the backend, not corrected.`;
    },
    driverWeeks() {
      if (this.v2 && this.v2.driversByWeek) return Object.keys(this.v2.driversByWeek).map(Number).sort((a, b) => a - b);
      const d = this.v2 && this.v2.drivers && this.v2.drivers[this.variable];
      return d ? [d.week] : [52];
    },
    driverBlock() {
      if (!this.v2) return null;
      const byWeek = this.v2.driversByWeek && this.v2.driversByWeek[this.driverWeek];
      if (byWeek && byWeek[this.variable]) return byWeek[this.variable];
      const d = this.v2.drivers && this.v2.drivers[this.variable];
      return d && d.week === this.driverWeek ? d : d && !this.v2.driversByWeek ? d : null;
    },
    driversReason() {
      if (this.v2Unavailable) return this.v2Unavailable;
      if (this.v2 && this.v2.unavailable) {
        const u = this.v2.unavailable.find((x) => x.block === `drivers.${this.variable}`);
        if (u) return u.reason;
      }
      return "";
    },
  },
  methods: {
    ...mapActions([
      "setCountriesConsensus",
      "setDataConsensus",
      "setDataConsensusResult",
      "appleyForecast",
      "exportExcel",
      "setDataFeForecast",
      "setDataLastSemester",
      "setDataConsensusReportResult",
      "setDataConsensusReportResultEndogenize",
      "loadDiagnostics",
      "resetForecast",
    ]),
    equationFor(series) {
      return this.diagnosticsDoc ? (this.diagnosticsDoc.equations || []).find((e) => e.series === series) || null : null;
    },
    onCountry(v) {
      this.value = v;
    },
    reload() {
      this.setDataConsensus(this.value.consensusReport);
    },
    updateCell({ rowIndex, colIndex, value }) {
      this.rows[rowIndex].data[colIndex] = value;
      // China's GDP appears both as the local and the international row: keep them in sync
      if (this.value.name === "China") {
        const name = this.rows[rowIndex].name;
        const twin = name === "Real GDP growth (% yoy)" ? "China GDP growth (% yoy)" : name === "China GDP growth (% yoy)" ? "Real GDP growth (% yoy)" : null;
        if (twin) this.rows.forEach((r) => r.name === twin && (r.data[colIndex] = value));
      }
    },
    resetRow(i) {
      this.rows[i].data = clone(this.originals[i].data);
    },
    resetAll() {
      this.rows = clone(this.originals).map((r, i) => ({ ...r, disable: this.rows[i] && this.rows[i].disable }));
    },
    apply() {
      this.appliedRows = clone(this.rows);
      this.appliedEndogenous = this.endogenous;
      const payload = buildAppJson({ country: this.value.forecastTool, quarters: this.quarters, rows: this.rows, endogenous: this.endogenous });
      this.appleyForecast(payload);
    },
    async onExport(action) {
      this.exportMessage = "";
      this.exporting = true;
      try {
        if (action === "excel-client") {
          const { sheets, skipped } = buildExportSheets({
            country: this.value.name,
            quarters: this.quarters,
            rows: this.appliedRows || this.rows,
            originals: this.originals,
            result: this.result,
            baselineMap: this.baselineMap,
            v2: this.v2,
            diagnostics: this.diagnosticsDoc,
            meta: { ...this.metaCard, illustrative: this.illustrative },
            scenarioHash: this.hash,
            scenarioLabel: this.scenarioLabel,
          });
          const name = `MTF ${this.value.name} ${this.result.dates[0]} ${this.hash || "consensus"}`;
          downloadXlsx(name, sheets);
          this.exportMessage = `Workbook downloaded (${sheets.length} sheets${skipped.length ? "; omitted: " + skipped.join(", ") : ""}).`;
        } else if (action === "excel-server") {
          // legacy server export: input table + result table, 2-decimal strings as before
          const data2 = this.result.rows.map((r) => ({ name: r.name, data: r.data.map((v) => (v == null ? "" : v.toFixed(2))), header: false }));
          const url = await this.exportExcel({ data: JSON.stringify(this.appliedRows || this.rows), headers: [...this.quarters], data2: JSON.stringify(data2), headers2: [...this.result.dates] });
          this.exportMessage = url ? "Server export opened in a new tab." : "The server did not return a file (server export is unavailable in this environment).";
        } else if (action === "png-copy" || action === "png-download") {
          const chart = this.$refs.chart;
          const svg = chart && chart.toSvg ? chart.toSvg(1200, 520) : null;
          if (!svg) throw new Error("The chart is not rendered.");
          const blob = await this.svgToPngBlob(svg, 1200, 520);
          const filename = `MTF ${this.value.name} ${this.variable}.png`;
          let copied = false;
          if (action === "png-copy" && navigator.clipboard && window.ClipboardItem) {
            try {
              await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);
              copied = true;
              this.exportMessage = "Chart copied to the clipboard as PNG.";
            } catch (e) {
              // permission denied or unsupported: fall back to a download and say so
              this.downloadBlob(blob, filename);
              this.exportMessage = `Clipboard access was denied (${e.message || e}); the PNG was downloaded instead.`;
              copied = true;
            }
          }
          if (!copied) {
            this.downloadBlob(blob, filename);
            this.exportMessage = action === "png-copy" ? "Clipboard images are not supported here; the PNG was downloaded instead." : "PNG downloaded.";
          }
        }
      } catch (e) {
        console.error("[export]", e);
        this.exportMessage = `Export failed: ${e.message || e}`;
      } finally {
        this.exporting = false;
      }
    },
    svgToPngBlob(svgMarkup, width, height) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(new Blob([svgMarkup], { type: "image/svg+xml;charset=utf-8" }));
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = width * 2;
          canvas.height = height * 2;
          const ctx = canvas.getContext("2d");
          ctx.fillStyle = getComputedStyle(document.documentElement).getPropertyValue("--bg-1").trim() || "#ffffff";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          URL.revokeObjectURL(url);
          canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Canvas export failed."))), "image/png");
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error("The chart SVG could not be rasterised."));
        };
        img.src = url;
      });
    },
    downloadBlob(blob, filename) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    },
    startTimer() {
      this.stopTimer();
      this.elapsed = 0;
      this.elapsedTimer = setInterval(() => {
        const t0 = this.getForecastLoadStartedAt || Date.now();
        this.elapsed = Math.floor((Date.now() - t0) / 1000);
      }, 500);
    },
    stopTimer() {
      if (this.elapsedTimer) clearInterval(this.elapsedTimer);
      this.elapsedTimer = null;
    },
  },
  watch: {
    value(v) {
      if (!v || !v.consensusReport) return;
      this.resetForecast();
      this.appliedRows = null;
      this.exportMessage = "";
      this.setDataConsensus(v.consensusReport);
      this.setDataConsensusResult(v.consensusReportResult);
      this.setDataFeForecast(v.consensusReportResult);
      this.setDataLastSemester(v.consensusReportResult);
      this.setDataConsensusReportResult(v.consensusReportResult);
      this.setDataConsensusReportResultEndogenize(v.consensusReportResult);
      this.loadDiagnostics(v.forecastTool || v.name);
    },
    getcontriesConsensus(val) {
      if (val && val.length && (!this.value || !this.value.name)) this.value = this.countries[0] || val[0];
    },
    getdataConsensus(val) {
      const t = parseConsensusTable(val);
      if (!t.rows.length) return;
      this.quarters = t.quarters;
      this.originals = clone(t.rows);
      this.rows = clone(t.rows).map((r) => ({ ...r, disable: this.endogenous && r.name === "USA - S&P500" }));
    },
    endogenous(val) {
      this.rows.forEach((r) => (r.disable = val && r.name === "USA - S&P500"));
    },
    status(s) {
      if (s === "loading") this.startTimer();
      else this.stopTimer();
    },
    "result.variables"(vars) {
      if (vars.length && !vars.includes(this.variable)) this.variable = vars[0];
    },
    getUrlExport(val) {
      if (val && val !== "") window.open(val, "_blank");
    },
  },
  mounted() {
    this.setCountriesConsensus();
  },
  beforeUnmount() {
    this.stopTimer();
  },
};
</script>
